import { supabase } from "@/integrations/supabase/client";
import {
  PaymentRecord,
  PaymentRecordFormData,
  EnhancedInvoice,
  PaymentSummary,
  PaymentAnalytics,
} from "@/types/invoice";
import { InvoiceCreditService } from "./InvoiceCreditService";

export class PaymentService {
  /**
   * Create a new payment record
   */
  static async createPaymentRecord(
    data: PaymentRecordFormData,
    repairShopId: string,
    createdBy: string
  ): Promise<PaymentRecord> {
    try {
      const { data: insertedData, error } = await supabase
        .from("payment_records")
        .insert({
          invoice_id: data.invoiceId,
          payment_date: data.paymentDate.toISOString().split("T")[0],
          amount: data.amount,
          payment_method: data.paymentMethod,
          reference_number: data.referenceNumber,
          notes: data.notes,
          repair_shop_id: repairShopId,
          created_by: createdBy,
        })
        .select()
        .single();

      if (error) throw error;

      // Trigger payment status recalculation
      await this.recalculateInvoicePaymentStatus(data.invoiceId);

      // Update client credit for the payment
      const invoice = await this.getInvoiceForPayment(data.invoiceId);
      if (invoice?.client_id) {
        await InvoiceCreditService.handleInvoicePayment(
          data.invoiceId,
          invoice.client_id,
          data.amount,
          data.paymentMethod,
          repairShopId,
          createdBy
        );
      }

      return this.mapPaymentRecordFromDB(insertedData);
    } catch (error) {
      console.error("Error creating payment record:", error);
      throw error;
    }
  }

  /**
   * Update an existing payment record
   */
  static async updatePaymentRecord(
    id: string,
    data: Partial<PaymentRecordFormData>
  ): Promise<void> {
    try {
      const updateData: any = {};

      if (data.paymentDate)
        updateData.payment_date = data.paymentDate.toISOString().split("T")[0];
      if (data.amount !== undefined) updateData.amount = data.amount;
      if (data.paymentMethod) updateData.payment_method = data.paymentMethod;
      if (data.referenceNumber !== undefined)
        updateData.reference_number = data.referenceNumber;
      if (data.notes !== undefined) updateData.notes = data.notes;

      updateData.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from("payment_records")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      // Get the invoice ID to recalculate payment status
      const { data: paymentRecord } = await supabase
        .from("payment_records")
        .select("invoice_id")
        .eq("id", id)
        .single();

      if (paymentRecord) {
        await this.recalculateInvoicePaymentStatus(paymentRecord.invoice_id);
      }
    } catch (error) {
      console.error("Error updating payment record:", error);
      throw error;
    }
  }

  /**
   * Delete a payment record
   */
  static async deletePaymentRecord(id: string): Promise<void> {
    try {
      // Get the invoice ID before deletion
      const { data: paymentRecord } = await supabase
        .from("payment_records")
        .select("invoice_id")
        .eq("id", id)
        .single();

      const { error } = await supabase
        .from("payment_records")
        .delete()
        .eq("id", id);

      if (error) throw error;

      // Recalculate payment status for the invoice
      if (paymentRecord) {
        await this.recalculateInvoicePaymentStatus(paymentRecord.invoice_id);
      }
    } catch (error) {
      console.error("Error deleting payment record:", error);
      throw error;
    }
  }

  /**
   * Get payment records for an invoice
   */
  static async getPaymentRecordsForInvoice(
    invoiceId: string
  ): Promise<PaymentRecord[]> {
    try {
      const { data, error } = await supabase
        .from("payment_records")
        .select("*")
        .eq("invoice_id", invoiceId)
        .order("payment_date", { ascending: false });

      if (error) throw error;

      return data.map(this.mapPaymentRecordFromDB);
    } catch (error) {
      console.error("Error fetching payment records:", error);
      throw error;
    }
  }

  /**
   * Get payment summary for an invoice
   */
  static async getPaymentSummary(invoiceId: string): Promise<PaymentSummary> {
    try {
      const paymentRecords = await this.getPaymentRecordsForInvoice(invoiceId);

      const totalPaid = paymentRecords.reduce(
        (sum, record) => sum + record.amount,
        0
      );
      const paymentCount = paymentRecords.length;
      const lastPaymentDate =
        paymentRecords.length > 0 ? paymentRecords[0].paymentDate : undefined;

      // Group by payment method
      const paymentMethods = paymentRecords.reduce((acc, record) => {
        const existing = acc.find(
          (item) => item.method === record.paymentMethod
        );
        if (existing) {
          existing.amount += record.amount;
          existing.count += 1;
        } else {
          acc.push({
            method: record.paymentMethod,
            amount: record.amount,
            count: 1,
          });
        }
        return acc;
      }, [] as { method: string; amount: number; count: number }[]);

      return {
        totalPaid,
        paymentCount,
        lastPaymentDate,
        paymentMethods,
      };
    } catch (error) {
      console.error("Error calculating payment summary:", error);
      throw error;
    }
  }

  /**
   * Recalculate payment status for an invoice
   */
  static async recalculateInvoicePaymentStatus(
    invoiceId: string
  ): Promise<void> {
    try {
      // Use the database function to recalculate payment status
      const { error } = await supabase.rpc("calculate_invoice_payment_status", {
        invoice_uuid: invoiceId,
      });

      if (error) throw error;
    } catch (error) {
      console.error("Error recalculating payment status:", error);
      throw error;
    }
  }

  /**
   * Detect and update overdue invoices
   */
  static async updateOverdueInvoices(): Promise<number> {
    try {
      const { data, error } = await supabase.rpc("update_overdue_invoices");

      if (error) throw error;

      return data || 0;
    } catch (error) {
      console.error("Error updating overdue invoices:", error);
      throw error;
    }
  }

  /**
   * Get overdue invoices for a repair shop
   */
  static async getOverdueInvoices(
    repairShopId: string
  ): Promise<EnhancedInvoice[]> {
    try {
      const { data, error } = await supabase
        .from("overdue_invoices")
        .select(
          `
          *,
          client:clients(*),
          items:invoice_items(*)
        `
        )
        .eq("repair_shop_id", repairShopId);

      if (error) throw error;

      return data.map(this.mapInvoiceFromDB);
    } catch (error) {
      console.error("Error fetching overdue invoices:", error);
      throw error;
    }
  }

  /**
   * Get payment analytics for a repair shop
   */
  static async getPaymentAnalytics(
    repairShopId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<PaymentAnalytics> {
    try {
      // Build date filter
      let query = supabase
        .from("payment_records")
        .select(
          `
          *,
          invoice:invoices!inner(repair_shop_id)
        `
        )
        .eq("invoice.repair_shop_id", repairShopId);

      if (startDate) {
        query = query.gte(
          "payment_date",
          startDate.toISOString().split("T")[0]
        );
      }
      if (endDate) {
        query = query.lte("payment_date", endDate.toISOString().split("T")[0]);
      }

      const { data: payments, error } = await query;

      if (error) throw error;

      // Get invoice totals for the same period
      let invoiceQuery = supabase
        .from("invoices")
        .select("total_amount, created_at, paid_amount, remaining_amount")
        .eq("repair_shop_id", repairShopId);

      if (startDate) {
        invoiceQuery = invoiceQuery.gte("created_at", startDate.toISOString());
      }
      if (endDate) {
        invoiceQuery = invoiceQuery.lte("created_at", endDate.toISOString());
      }

      const { data: invoices, error: invoiceError } = await invoiceQuery;

      if (invoiceError) throw invoiceError;

      // Calculate analytics
      const totalRevenue = invoices.reduce(
        (sum, inv) => sum + inv.total_amount,
        0
      );
      const totalPaid = payments.reduce(
        (sum, payment) => sum + payment.amount,
        0
      );
      const totalOutstanding = invoices.reduce(
        (sum, inv) => sum + (inv.remaining_amount || 0),
        0
      );

      // Calculate average payment time (simplified)
      const averagePaymentTime = 15; // TODO: Implement proper calculation

      // Payment method breakdown
      const paymentMethodBreakdown = payments.reduce((acc, payment) => {
        const existing = acc.find(
          (item) => item.method === payment.payment_method
        );
        if (existing) {
          existing.amount += payment.amount;
        } else {
          acc.push({
            method: payment.payment_method,
            amount: payment.amount,
            percentage: 0, // Will be calculated below
          });
        }
        return acc;
      }, [] as { method: string; amount: number; percentage: number }[]);

      // Calculate percentages
      paymentMethodBreakdown.forEach((item) => {
        item.percentage = totalPaid > 0 ? (item.amount / totalPaid) * 100 : 0;
      });

      // Monthly trends (simplified - last 6 months)
      const monthlyTrends = this.calculateMonthlyTrends(invoices, payments);

      return {
        totalRevenue,
        totalPaid,
        totalOutstanding,
        averagePaymentTime,
        paymentMethodBreakdown,
        monthlyTrends,
      };
    } catch (error) {
      console.error("Error calculating payment analytics:", error);
      throw error;
    }
  }

  /**
   * Validate payment amount against invoice
   */
  static validatePaymentAmount(
    paymentAmount: number,
    invoiceTotal: number,
    alreadyPaid: number,
    allowOverpayment: boolean = true
  ): { isValid: boolean; error?: string } {
    if (paymentAmount <= 0) {
      return {
        isValid: false,
        error: "Payment amount must be greater than zero",
      };
    }

    const remaining = invoiceTotal - alreadyPaid;

    if (!allowOverpayment && paymentAmount > remaining) {
      return {
        isValid: false,
        error: `Payment amount exceeds remaining balance of ${remaining.toFixed(
          3
        )} TND`,
      };
    }

    // Allow overpayment up to 10% of invoice total
    const maxAllowed = remaining + invoiceTotal * 0.1;
    if (paymentAmount > maxAllowed) {
      return {
        isValid: false,
        error: `Payment amount exceeds maximum allowed overpayment`,
      };
    }

    return { isValid: true };
  }

  /**
   * Calculate monthly trends
   */
  private static calculateMonthlyTrends(
    invoices: any[],
    payments: any[]
  ): { month: string; invoiced: number; paid: number; outstanding: number }[] {
    const months = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toISOString().slice(0, 7); // YYYY-MM
      const monthName = date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });

      const monthInvoices = invoices.filter((inv) =>
        inv.created_at.startsWith(monthKey)
      );

      const monthPayments = payments.filter((payment) =>
        payment.payment_date.startsWith(monthKey)
      );

      const invoiced = monthInvoices.reduce(
        (sum, inv) => sum + inv.total_amount,
        0
      );
      const paid = monthPayments.reduce(
        (sum, payment) => sum + payment.amount,
        0
      );
      const outstanding = monthInvoices.reduce(
        (sum, inv) => sum + (inv.remaining_amount || 0),
        0
      );

      months.push({
        month: monthName,
        invoiced,
        paid,
        outstanding,
      });
    }

    return months;
  }

  /**
   * Get invoice information for payment processing
   */
  private static async getInvoiceForPayment(invoiceId: string): Promise<any> {
    const { data, error } = await supabase
      .from("invoices")
      .select("id, client_id, total_amount")
      .eq("id", invoiceId)
      .single();

    if (error) {
      console.error("Error fetching invoice for payment:", error);
      return null;
    }

    return data;
  }

  /**
   * Map payment record from database format
   */
  private static mapPaymentRecordFromDB(dbRecord: any): PaymentRecord {
    return {
      id: dbRecord.id,
      invoiceId: dbRecord.invoice_id,
      paymentDate: new Date(dbRecord.payment_date),
      amount: dbRecord.amount,
      paymentMethod: dbRecord.payment_method,
      referenceNumber: dbRecord.reference_number,
      notes: dbRecord.notes,
      repairShopId: dbRecord.repair_shop_id,
      createdBy: dbRecord.created_by,
      createdAt: new Date(dbRecord.created_at),
      updatedAt: new Date(dbRecord.updated_at),
    };
  }

  /**
   * Map invoice from database format (simplified)
   */
  private static mapInvoiceFromDB(dbRecord: any): EnhancedInvoice {
    return {
      id: dbRecord.id,
      invoiceNumber: dbRecord.invoice_number,
      clientId: dbRecord.client_id,
      clientName: dbRecord.client_name,
      clientPhone: dbRecord.client_phone,
      clientEmail: dbRecord.client_email,
      clientAddress: dbRecord.client_address,
      subtotal: dbRecord.subtotal,
      taxRate: dbRecord.tax_rate,
      taxAmount: dbRecord.tax_amount,
      discountAmount: dbRecord.discount_amount,
      totalAmount: dbRecord.total_amount,
      status: dbRecord.status,
      paymentStatus: dbRecord.payment_status || "unpaid",
      paidAmount: dbRecord.paid_amount || 0,
      remainingAmount: dbRecord.remaining_amount || dbRecord.total_amount,
      lastPaymentDate: dbRecord.last_payment_date
        ? new Date(dbRecord.last_payment_date)
        : undefined,
      templateId: dbRecord.template_id,
      dueDate: dbRecord.due_date ? new Date(dbRecord.due_date) : undefined,
      notes: dbRecord.notes,
      termsAndConditions: dbRecord.terms_and_conditions,
      repairShopId: dbRecord.repair_shop_id,
      createdBy: dbRecord.created_by,
      createdAt: new Date(dbRecord.created_at),
      updatedAt: new Date(dbRecord.updated_at),
      items:
        dbRecord.items?.map((item: any) => ({
          id: item.id,
          invoiceId: item.invoice_id,
          productId: item.product_id,
          productName: item.product_name,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercentage: item.discount_percentage,
          discountAmount: item.discount_amount,
          totalPrice: item.total_price,
          createdAt: new Date(item.created_at),
          updatedAt: new Date(item.updated_at),
        })) || [],
      daysOverdue: dbRecord.days_overdue || 0,
      isOverdue: dbRecord.days_overdue > 0,
    };
  }
}
